<template>
  <div class="nuclear-fission-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$E = Δmc²$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 反应堆模拟区 -->
      <div class="canvas-3d-wrapper interactive">
        <svg width="100%" height="240" viewBox="0 0 400 240">
           <!-- 背景 -->
           <rect width="400" height="240" fill="rgba(0,0,0,0.4)" rx="12" />
           
           <!-- 原子核 -->
           <g v-for="atom in atoms" :key="atom.id">
              <circle v-if="!atom.split" :cx="atom.x" :cy="atom.y" r="8" fill="#e67e22" stroke="#d35400" />
              <g v-else>
                 <!-- 裂变后的碎片 -->
                 <circle :cx="atom.x-10" :cy="atom.y" r="5" fill="#f1c40f" opacity="0.6" />
                 <circle :cx="atom.x+10" :cy="atom.y" r="5" fill="#f1c40f" opacity="0.6" />
                 <!-- 闪光特效 -->
                 <circle :cx="atom.x" :cy="atom.y" r="20" fill="rgba(255,255,255,0.2)">
                    <animate attributeName="r" from="10" to="30" dur="0.2s" />
                    <animate attributeName="opacity" from="1" to="0" dur="0.2s" />
                 </circle>
              </g>
           </g>

           <!-- 中子束 -->
           <g v-for="n in neutrons" :key="n.id">
              <circle :cx="n.x" :cy="n.y" r="2" fill="#fff" />
           </g>
        </svg>

        <div class="hud-center">
           <div class="power-bar">
              <div class="fill" :style="{ width: chainReactionProgress + '%', background: barColor }"></div>
           </div>
           <div class="label">能量活跃度: {{ (chainReactionProgress).toFixed(1) }}%</div>
        </div>
      </div>

      <!-- 参数控制面板 -->
      <div class="bottom-controls">
         <div class="action-card glass">
            <el-button type="danger" size="small" @click="triggerFission" :disabled="neutrons.length > 0">发射初始中子束</el-button>
            <el-button size="small" @click="reset">重置核心</el-button>
         </div>

         <div class="theory-note">
            <i class="el-icon-warning-outline"></i>
            <p><strong>链式反应：</strong>一个铀原子核裂变产生能量的同时，会释放2-3个中子。这些中子继续撞击其他原子核，引发连锁反应。控制中子密度是核能利用的关键。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NuclearFissionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      atoms: [],
      neutrons: [],
      running: true,
      nextId: 0,
      animFrame: null
    }
  },
  computed: {
    chainReactionProgress() {
      const splitAtoms = this.atoms.filter(a => a.split).length;
      return (splitAtoms / this.atoms.length || 0) * 100;
    },
    barColor() {
       if (this.chainReactionProgress > 80) return '#ff4757';
       if (this.chainReactionProgress > 30) return '#ffd700';
       return '#2ed573';
    }
  },
  mounted() { this.reset(); },
  beforeDestroy() { 
    this.running = false;
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
  },
  methods: {
    reset() {
      this.atoms = [];
      this.neutrons = [];
      for(let i=0; i<4; i++) {
          for(let j=0; j<4; j++) {
              this.atoms.push({ id: this.nextId++, x: 80 + i * 80, y: 45 + j * 50, split: false });
          }
      }
    },
    triggerFission() {
      this.neutrons.push({ id: this.nextId++, x: 0, y: 120, vx: 5, vy: (Math.random()-0.5) * 2 });
      this.animate();
    },
    animate() {
      if (!this.running || this.neutrons.length === 0) return;

      const newNeutrons = [];
      const stillActive = [];

      this.neutrons.forEach((n) => {
          n.x += n.vx;
          n.y += n.vy;

          let hit = false;
          // Check collision with atoms
          for (let i = 0; i < this.atoms.length; i++) {
              const atom = this.atoms[i];
              if (!atom.split) {
                  const dist = Math.hypot(n.x - atom.x, n.y - atom.y);
                  if (dist < 10) {
                      atom.split = true;
                      hit = true;
                      // Release new neutrons
                      for(let k=0; k<2; k++) {
                          newNeutrons.push({
                              id: this.nextId++,
                              x: atom.x,
                              y: atom.y,
                              vx: (Math.random() * 4 + 2) * (Math.random() > 0.5 ? 1 : -1),
                              vy: (Math.random() - 0.5) * 8
                          });
                      }
                      break;
                  }
              }
          }

          // Boundary check
          if (!hit && n.x >= 0 && n.x <= 400 && n.y >= 0 && n.y <= 240) {
              stillActive.push(n);
          }
      });

      this.neutrons = [...stillActive, ...newNeutrons];

      if (this.neutrons.length > 0) {
          this.animFrame = requestAnimationFrame(this.animate);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.nuclear-fission-viz {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}
.viz-header { display: flex; justify-content: space-between; align-items: center; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { color: #ff6b6b; background: rgba(255, 107, 107, 0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.canvas-3d-wrapper {
  flex: 1.5; position: relative;
  .hud-center { position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); width: 250px; text-align: center;
    .power-bar { height: 6px; background: rgba(255,255,255,0.1); border-radius: 3px; overflow: hidden; margin-bottom: 5px; .fill { height: 100%; transition: width 0.3s, background 0.3s; } }
    .label { font-size: 0.6rem; color: #aaa; text-transform: uppercase; }
  }
}
.bottom-controls { flex: 0.5; display: flex; flex-direction: column; gap: 12px; }
.action-card { display: flex; gap: 10px; justify-content: center; background: rgba(255,255,255,0.03); padding: 15px; border-radius: 12px; }
.theory-note { background: rgba(255, 71, 87, 0.05); padding: 12px 18px; border-radius: 12px; display: flex; gap: 15px; i { color: #ff4757; font-size: 1.2rem; margin-top: 2px; } p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } } }
</style>
